/* istanbul ignore file */
import React, { useEffect, useState } from 'react'
import LanguageStrings from '../../../../i18n/locales'
import { Grid, Button, CircularProgress } from '@mui/material'
import { styled } from '@mui/system'
import PerformanceIndicatorsTable from './PerformanceIndicatorsTable'
import CustomToast from '../ToastComponent'
import { connect, type ConnectedProps } from 'react-redux'
import SearchIcon from '../../../../assets/SearchIcon.png'
import { saveDefaultKpiConfig } from '../../redux/actionCreators'
import { type DefaultIndicatorState } from '../../redux/addDefaultIndicatorSlice'
import { type RootState } from '../../../../store'
import Modal from '../../../../components/Modal'
import { APIConfiguration } from '../../../../utils/apiConfiguration'
import { defaultIndicatorReset } from '../../redux/addDefaultIndicatorSlice'
import { useDispatch } from 'react-redux'
import './PerformanceIndicators.css'

const httpStatus = APIConfiguration
const AddDeviceStrings =
  LanguageStrings().DeviceManagementCollectionStrings.AddDeviceComponent

const HContainer = styled('div')({
  marginTop: '5px',
  marginBottom: '5px'
})
const Heading5 = styled('h5')({
  color: '#F0F0F0',
  fontFamily: 'Honeywell Sans Web',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 800,
  lineHeight: '30px'
})
const Span = styled('span')({
  color: '#B0B0B0',
  textAlign: 'right',
  fontFeatureSettings: 'clig off, liga off',
  fontFamily: 'Honeywell Sans Web',
  fontStyle: 'normal',
  fontSize: '16px'
})
const BContainer = styled('div')({
  width: '1250px',
  height: '453px',
  padding: '24px 0px',
  gap: '16px',
  flexShrink: 0,
  borderRadius: '10px',
  border: '1px solid var(--Greyscale-Grey-n7-101010, #101010)',
  background: 'var(--background-dark-component-container-1, #272727)',
  color: '#F0F0F0'
})
const Input = styled('input')({
  width: '248px',
  height: '35px',
  padding: '8px 8px 8px 40px',
  borderRadius: '5px',
  border: '1px solid #ccc',
  backgroundImage: `url(${SearchIcon})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '8px center',
  backgroundColor: '#3d3d3d',
  '::placeholder': {
    color: 'var(--typography-dark-secondary, #D0D0D0)',
    fontFeatureSettings: 'clig off, liga off',
    fontFamily: '"Honeywell Sans Web"',
    fontSize: '14px',
    fontStyle: 'italic',
    fontWeight: 500,
    lineHeight: '20px'
  }
})

export interface Indicator {
  id: string
  kpiDefinitionId: string
  kpiName: string
  assetId: string | null
  isKpiEnabled: boolean
  isAlarmApplicable: boolean
  isAlarmEnabled: boolean
  alarmHigherLimitSeverity: string | null
  alarmLowerLimitSeverity: string | null
  isCustomKpi: boolean
  tenantId: string | null
  unit: string | null
  isDefaultConfig: boolean
  executionFrequencyMinutes: number | null
  alarmLowerLimit: number | null
  alarmLowerLimitStateText: string | null
  alarmLowerLimitTextColorId: string | null
  alarmLowerLimitColorName: string | null
  alarmLowerLimitColorValue: string | null
  alarmLowerLimitSeverityId: string | null
  alarmLowerLimitSeverityLevel: string | null
  alarmLowerLimitLabelId: string | null
  alarmLowerLimitLabelName: string | null
  alarmLowerLimitRecommendationText: string
  alarmHigherLimit: string | null
  alarmHigherLimitStateText: string
  alarmHigherLimitTextColorId: string | null
  alarmHigherLimitColorName: string | null
  alarmHigherLimitColorValue: string | null
  alarmHigherLimitSeverityId: string | null
  alarmHigherLimitSeverityLevel: string | null
  alarmHigherLimitLabelId: string | null
  alarmHigherLimitLabelName: string | null
  alarmHigherLimitRecommendationText: string
  isTotalizer?: boolean
  isLowerLimitAlarmEnabled: boolean | false
  lowerLimitOperator: string | null
  isHigherLimitAlarmEnabled: boolean | false
  higherLimitOperator: string | null
}

interface ParentProps {
  assetId: string
  assetTypeId: string
  indicators: Indicator[] | null
  instanceType: 'standard' | 'custom'
  setIndicators: (indicatorList: Indicator[]) => void
  setIsError: React.Dispatch<React.SetStateAction<any>>
}

const PerformanceIndicators = (
  props: PropsFromRedux & ParentProps
): JSX.Element => {
  const [stdIndicators, setStandardIndicators] = React.useState<any>([])
  const [customIndicators, setCustomIndicators] = React.useState<any>([])
  const [selectAllStd, setSelectAllStd] = useState<boolean>(false)
  const [selectAllCustom, setSelectAllCustom] = useState<boolean>(false)
  const [searchStdKpi, setSearchStdKpi] = useState<string>('')
  const [searchCustomKpi, setSearchCustomKpi] = useState<string>('')
  const [isDefaultStdSaved, setIsDefaultStdSaved] = React.useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (Array.isArray(props.indicators)) {
      const stdIndicators = props.indicators.filter(
        (indicator: Indicator) => !indicator.isCustomKpi
      )
      setStandardIndicators(stdIndicators)
      const customIndicators = props.indicators.filter(
        (indicator: Indicator) => indicator.isCustomKpi
      )
      setCustomIndicators(customIndicators)
    }
  }, [props.indicators])

  const handleSearchStdKpi = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchStdKpi(event.target.value)
  }
  const handleSearchCustomKpi = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchCustomKpi(event.target.value)
  }
  const saveDefaultStdConfig = (): void => {
    dispatch(defaultIndicatorReset())
    const indicators = [...stdIndicators, ...customIndicators]
    props.saveDefaultKpiConfig(indicators, props.assetTypeId)
    setIsDefaultStdSaved(true)
  }

  // istanbul ignore next
  const handleDefaultKpiWithFailure = (): JSX.Element | null => {
    if (
      !props.defaultIndicatorState.isLoading &&
      props.defaultIndicatorState.httpStatus === httpStatus.CREATED_HTTP_STATUS
    ) {
      CustomToast({
        type: 'success',
        heading: AddDeviceStrings.DefaultViewSaved,
        message: AddDeviceStrings.KpiAddedSuccessfully
      })
      setIsDefaultStdSaved(false)
      return null
    } else if (props.defaultIndicatorState.isLoading) {
      return (
        <Modal
          titleStyle={{ color: '#fff' }}
          title={AddDeviceStrings.SaveDefaultView}
        >
          <div className="CircularprogressAmi">
            <CircularProgress />
          </div>
        </Modal>
      )
    } else if (
      !props.defaultIndicatorState.isLoading &&
      props.defaultIndicatorState?.error !== ''
    ) {
      CustomToast({
        type: 'error',
        heading: AddDeviceStrings.SaveDefaultViewError,
        message: props.defaultIndicatorState?.error
      })
      setIsDefaultStdSaved(false)
      return null
    }
    return null
  }

  const toggleSelectAllStd = (): void => {
    const newSelectAllStd = !selectAllStd
    setSelectAllStd(newSelectAllStd)
    const updatedIndicators = stdIndicators.map((indicator: any) => ({
      ...indicator,
      isKpiEnabled: newSelectAllStd,
      ...(newSelectAllStd
        ? {
            executionFrequencyMinutes: indicator.isTotalizer === true
              ? 1
              : (indicator.executionFrequencyMinutes ?? 1)
          }
        : {
            executionFrequencyMinutes: indicator.isTotalizer === true ? 1 : 1,
            isAlarmEnabled: false
          })
    }))
    props.setIndicators([...updatedIndicators, ...customIndicators])
  }

  const toggleStdSelectIndicator = (id: string): void => {
    const updatedIndicators = stdIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, isKpiEnabled: !indicator.isKpiEnabled }
        : indicator
    )
    const allSelected = updatedIndicators.every(
      (indicator: any) => indicator.isKpiEnabled
    )
    setSelectAllStd(allSelected)
    const parametersToUpdate: Indicator = updatedIndicators.find(
      (indicator: Indicator) => indicator.kpiDefinitionId === id
    )
    if (parametersToUpdate != null) {
      if (!parametersToUpdate.isKpiEnabled) {
        // When disabling KPI, reset all values
        const resetValues = {
          executionFrequencyMinutes: parametersToUpdate.isTotalizer === true ? 1 : 1,
          isAlarmEnabled: false
        }
        Object.assign(parametersToUpdate, resetValues)
      } else {
        // When enabling KPI, set default execution frequency if not already set
        if (parametersToUpdate.executionFrequencyMinutes === null || parametersToUpdate.executionFrequencyMinutes === undefined) {
          // For totalizers, always set to 1 minute, for others set to 1
          parametersToUpdate.executionFrequencyMinutes = 1
        } else if (parametersToUpdate.isTotalizer === true) {
          // If it's a totalizer, always ensure it's set to 1 minute
          parametersToUpdate.executionFrequencyMinutes = 1
        }
      }
    }
    props.setIndicators([...updatedIndicators, ...customIndicators])
  }
  const handleStdAlarmChange = (id: string): void => {
    const updatedIndicators = stdIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, isAlarmEnabled: !indicator.isAlarmEnabled }
        : indicator
    )
    const parametersToUpdate: Indicator = updatedIndicators.find(
      (indicator: Indicator) => indicator.kpiDefinitionId === id
    )
    if (parametersToUpdate != null && !parametersToUpdate.isAlarmEnabled) {
      const resetValues = {
        alarmLowerLimit: '',
        alarmHigherLimit: ''
      }
      Object.assign(parametersToUpdate, resetValues)
    }
    props.setIndicators([...updatedIndicators, ...customIndicators])
  }
  /* istanbul ignore next */
  const handleStdChangeTextBox = (
    id: string | number,
    propertyName: string,
    value: string | number
  ): void => {
    const parsedValue: number | string = value
    // Validation for alarmLowerLimit and alarmHigherLimit
    if (
      propertyName === 'alarmLowerLimit' ||
      propertyName === 'alarmHigherLimit'
    ) {
      if (typeof value === 'number') {
        const minValue = -2147483648.0
        const maxValue = 2147483647.0
        if (value < minValue || value > maxValue) {
          props.setIsError(true)
          CustomToast({
            type: 'error',
            heading: AddDeviceStrings.Error,
            message: `${String(
              AddDeviceStrings.ValueMustBeBetween
            )} ${minValue} and ${maxValue}`
          })
          return
        }
      }
    }
    // Reset Error State
    props.setIsError(false)

    // Update Indicators
    const updatedIndicators = stdIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, [propertyName]: parsedValue }
        : indicator
    )
    // Set Updated Indicators
    props.setIndicators([...updatedIndicators, ...customIndicators])
  }
  const handleStdAlarmSelect = (
    id: string | number,
    propertyName: string,
    value: string
  ): void => {
    const updatedIndicators = stdIndicators.map((indicator: Indicator) => {
      if (indicator.kpiDefinitionId === id) {
        return { ...indicator, [propertyName]: value }
      }
      return indicator
    })
    props.setIndicators([...updatedIndicators, ...customIndicators])
  }

  const toggleSelectAllCustom = (): void => {
    const newSelectAllCustom = !selectAllCustom
    setSelectAllCustom(newSelectAllCustom)
    const updatedCustomIndicators = customIndicators.map((indicator: any) => ({
      ...indicator,
      isKpiEnabled: newSelectAllCustom,
      ...(newSelectAllCustom
        ? {
            executionFrequencyMinutes: indicator.isTotalizer === true
              ? 1
              : (indicator.executionFrequencyMinutes ?? 1)
          }
        : {
            executionFrequencyMinutes: indicator.isTotalizer === true ? 1 : 1,
            isAlarmEnabled: false
          })
    }))
    props.setIndicators([...stdIndicators, ...updatedCustomIndicators])
  }
  const toggleCustomSelectIndicator = (id: string): void => {
    const updatedIndicators = customIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, isKpiEnabled: !indicator.isKpiEnabled }
        : indicator
    )
    const allSelected = updatedIndicators.every(
      (indicator: any) => indicator.isKpiEnabled
    )
    setSelectAllCustom(allSelected)
    const parametersToUpdate: Indicator = updatedIndicators.find(
      (indicator: Indicator) => indicator.kpiDefinitionId === id
    )
    if (parametersToUpdate != null) {
      if (!parametersToUpdate.isKpiEnabled) {
        // When disabling KPI, reset all values
        const resetValues = {
          executionFrequencyMinutes: parametersToUpdate.isTotalizer === true ? 1 : 1,
          isAlarmEnabled: false
        }
        Object.assign(parametersToUpdate, resetValues)
      } else {
        // When enabling KPI, set default execution frequency if not already set
        if (parametersToUpdate.executionFrequencyMinutes === null || parametersToUpdate.executionFrequencyMinutes === undefined) {
          // For totalizers, always set to 1 minute, for others set to 1
          parametersToUpdate.executionFrequencyMinutes = 1
        } else if (parametersToUpdate.isTotalizer === true) {
          // If it's a totalizer, always ensure it's set to 1 minute
          parametersToUpdate.executionFrequencyMinutes = 1
        }
      }
    }
    props.setIndicators([...stdIndicators, ...updatedIndicators])
  }
  const handleCustomAlarmChange = (id: string): void => {
    const updatedIndicators = customIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, isAlarmEnabled: !indicator.isAlarmEnabled }
        : indicator
    )
    const parametersToUpdate: Indicator = updatedIndicators.find(
      (indicator: Indicator) => indicator.kpiDefinitionId === id
    )
    if (parametersToUpdate != null && !parametersToUpdate.isAlarmEnabled) {
      const resetValues = {
        alarmLowerLimit: '',
        alarmHigherLimit: ''
      }
      Object.assign(parametersToUpdate, resetValues)
    }
    props.setIndicators([...stdIndicators, ...updatedIndicators])
  }
  // istanbul ignore next
  const handleCustomChangeTextBox = (
    id: string | number,
    propertyName: string,
    value: string | number
  ): void => {
    let parsedValue: number | string = value
    // Validation for alarmLowerLimit and alarmHigherLimit
    if (propertyName === 'alarmLowerLimit' || propertyName === 'alarmHigherLimit') {
      if (typeof value === 'number') {
        const minValue = -2147483648.0
        const maxValue = 2147483647
        if (value < minValue || value > maxValue) {
          props.setIsError(true)
          CustomToast({
            type: 'error',
            heading: AddDeviceStrings.Error,
            message: `${String(
              AddDeviceStrings.ValueMustBeBetween
            )} ${minValue} and ${maxValue}`
          })
          return
        }
      } else if (typeof value === 'string' && value !== '') {
        parsedValue = propertyName === 'alarmLowerLimit' || propertyName === 'alarmHigherLimit'
          ? parseFloat(value)
          : parseInt(value, 10)
        if (isNaN(parsedValue)) {
          props.setIsError(true)
          return
        }
      }
    }
    props.setIsError(false)
    const updatedIndicators = customIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, [propertyName]: parsedValue }
        : indicator
    )
    props.setIndicators([...stdIndicators, ...updatedIndicators])
  }

  const handleCustomAlarmSelect = (
    id: string | number,
    propertyName: string,
    value: string
  ): void => {
    const updatedIndicators = customIndicators.map((indicator: Indicator) =>
      indicator.kpiDefinitionId === id
        ? { ...indicator, [propertyName]: value }
        : indicator
    )
    props.setIndicators([...stdIndicators, ...updatedIndicators])
  }
  const stdHeaders = [
    AddDeviceStrings.StandardKPIs,
    AddDeviceStrings.ExecutionFrequency,
    AddDeviceStrings.Alarm,
    ''
  ]

  const customHeaders = [
    AddDeviceStrings.CustomKPIs,
    AddDeviceStrings.ExecutionFrequency,
    AddDeviceStrings.Alarm,
    ''
  ]

  const filteredStdIndicators = stdIndicators.filter((indicator: any) =>
    indicator.kpiName.toLowerCase().includes(searchStdKpi.toLowerCase())
  )
  const filteredCustomIndicators = customIndicators.filter((indicator: any) =>
    indicator.kpiName.toLowerCase().includes(searchCustomKpi.toLowerCase())
  )
  const selectedStdCount = filteredStdIndicators.filter(
    (indicator: any) => indicator.isKpiEnabled
  ).length
  const selectedCustomCount = filteredCustomIndicators.filter(
    (indicator: any) => indicator.isKpiEnabled
  ).length

  const handleCancel = (id: string, isCustomKpiFlag: boolean): void => {
    // get previously stored data from localStorage
    let savedData: any = null
    const alarmData = localStorage.getItem('alarmData')
    if (alarmData !== null) {
      savedData = JSON.parse(alarmData)
    }

    // apply previously stored data if user clicked cancel btn, **add id check in "savedData" in future if needed
    if (savedData !== null) {
      if (isCustomKpiFlag) {
        const updatedIndicators = customIndicators.map((indicator: Indicator) =>
          indicator.kpiDefinitionId === id
            ? { ...indicator, ...savedData }
            : indicator
        )
        props.setIndicators([...stdIndicators, ...updatedIndicators])
      } else {
        const updatedIndicators = stdIndicators.map((indicator: Indicator) =>
          indicator.kpiDefinitionId === id
            ? { ...indicator, ...savedData }
            : indicator
        )
        props.setIndicators([...updatedIndicators, ...customIndicators])
      }
    }
  }

  return (
    <div className="standard-indicators">
      <Grid container>
        <Grid item xs={12}>
          <HContainer>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Heading5 data-testid="standard-text">
                {props.instanceType === 'standard'
                  ? AddDeviceStrings.StandardKPIs
                  : AddDeviceStrings.CustomKPIs}
              </Heading5>
              <Span>
                {props.instanceType === 'standard'
                  ? selectedStdCount
                  : selectedCustomCount}{' '}
                {AddDeviceStrings.Selected}
              </Span>
            </div>
          </HContainer>
          <BContainer>
            {props.instanceType === 'standard'
              ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      margin: '0px 20px 12px'
                    }}
                  >
                    <Input
                      type="search"
                      placeholder={AddDeviceStrings.SearchStandardKPI}
                      onChange={handleSearchStdKpi}
                      value={searchStdKpi}
                      data-testid="std-search-input"
                    />
                    <Button
                      className="save-default-btn"
                      style={{
                        fontFamily: 'Honeywell Sans Web',
                        textTransform: 'none',
                        padding: '8px 20px',
                        height: '35px'
                      }}
                      size="small"
                      data-testid="std-save-params"
                      variant="outlined"
                      disabled={false}
                      onClick={saveDefaultStdConfig}
                    >
                      {AddDeviceStrings.SaveAsDefaultView}
                    </Button>
                  </div>
                  <PerformanceIndicatorsTable
                    tableHeaders={stdHeaders}
                    indicators={filteredStdIndicators}
                    instanceType={props.instanceType}
                    selectedCount={selectedStdCount}
                    toggleSelectAll={toggleSelectAllStd}
                    toggleSelectIndicator={toggleStdSelectIndicator}
                    handleAlarmChange={handleStdAlarmChange}
                    handleChangeTextBox={handleStdChangeTextBox}
                    handleAlarmSelect={handleStdAlarmSelect}
                    handleCancel={handleCancel}
                  />
                </>
                )
              : (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      margin: '0px 20px 12px'
                    }}
                  >
                    <Input
                      type="search"
                      placeholder={AddDeviceStrings.SearchCustomKPI}
                      onChange={handleSearchCustomKpi}
                      value={searchCustomKpi}
                      data-testid="std-custom-input"
                    />
                    <Button
                      className="save-default-btn"
                      style={{
                        fontFamily: 'Honeywell Sans Web',
                        textTransform: 'none',
                        padding: '8px 20px',
                        height: '35px'
                      }}
                      size="small"
                      data-testid="custom-save-params"
                      variant="outlined"
                      disabled={false}
                      onClick={saveDefaultStdConfig}
                    >
                      {AddDeviceStrings.SaveAsDefaultView}
                    </Button>
                  </div>
                  <PerformanceIndicatorsTable
                    tableHeaders={customHeaders}
                    indicators={filteredCustomIndicators}
                    instanceType={props.instanceType}
                    selectedCount={selectedCustomCount}
                    toggleSelectAll={toggleSelectAllCustom}
                    toggleSelectIndicator={toggleCustomSelectIndicator}
                    handleAlarmChange={handleCustomAlarmChange}
                    handleChangeTextBox={handleCustomChangeTextBox}
                    handleAlarmSelect={handleCustomAlarmSelect}
                    handleCancel={handleCancel}
                  />
                </>
                )}
          </BContainer>
          {isDefaultStdSaved && handleDefaultKpiWithFailure()}
        </Grid>
      </Grid>
    </div>
  )
}

interface DispatchToProps {
  saveDefaultKpiConfig: (defaultKpiRequest: any, assetTypeId: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  saveDefaultKpiConfig: (defaultKpiRequest: any, assetTypeId: string) =>
    dispatch(saveDefaultKpiConfig(defaultKpiRequest, assetTypeId))
})

interface StateToProps {
  defaultIndicatorState: DefaultIndicatorState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  defaultIndicatorState: state.defaultIndicatorState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(PerformanceIndicators)
